/**
 * Closing Periods Service
 *
 * Data access layer for closing_periods table.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';

export interface ClosingPeriod {
  id: string;
  location_id: string;
  start_date: string;
  end_date: string;
  reason: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ClosingPeriodInsert {
  location_id: string;
  start_date: string;
  end_date: string;
  reason?: string | null;
}

export interface ClosingPeriodUpdate {
  start_date?: string;
  end_date?: string;
  reason?: string | null;
}

/**
 * Fetch all closing periods for a location
 */
export async function getClosingPeriodsByLocationId(locationId: string) {
  try {
    const { data, error } = await supabase
      .from('closing_periods')
      .select('*')
      .eq('location_id', locationId)
      .order('start_date', { ascending: true });

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Create a new closing period
 */
export async function createClosingPeriod(closingPeriod: ClosingPeriodInsert) {
  try {
    const { data, error } = await supabase
      .from('closing_periods')
      .insert(closingPeriod)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Update a closing period
 */
export async function updateClosingPeriod(id: string, updates: ClosingPeriodUpdate) {
  try {
    const { data, error } = await supabase
      .from('closing_periods')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Delete a closing period
 */
export async function deleteClosingPeriod(id: string) {
  try {
    const { error } = await supabase
      .from('closing_periods')
      .delete()
      .eq('id', id);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}
